import React, { Fragment } from 'react';
import { Box, Typography } from '@mui/material';
import {
  DiscountType,
  LandingpageProductResponse,
  TerminationRule,
} from '@shared/types/campaigns';
import useDateTime from '@hooks/use-datetime';
import styles from './CampaignSummary.module.css';
import { useAppSelector } from '@shared/store';
import { useTranslation } from 'next-i18next';
import { useFormatPrice } from '@components/Price';

const { PaymentToday } = styles;

interface CampaignSummaryProps {
  showFirstHalf: boolean;
  showSecondHalf: boolean;
  priceInformation: LandingpageProductResponse;
  forSecondStep?: boolean;
}

const CampaignSummary: React.FC<CampaignSummaryProps> = ({
  showFirstHalf,
  showSecondHalf,
  priceInformation,
  forSecondStep = false,
}) => {
  const {
    options: { currencySymbol },
  } = useAppSelector((state) => state.wordpress);

  const { format } = useDateTime();
  const { t } = useTranslation(['campaign', 'checkout']);

  const terminationRule = priceInformation.termination_rule;

  const formattedPrices = {
    planPrice: useFormatPrice({
      amount: priceInformation.plan_price / 100,
      noCurrencySymbol: true,
      showDecimals: false,
    }),
    planPriceRestMonth: useFormatPrice({
      amount: priceInformation.plan_price_rest_month / 100 || 0,
      noCurrencySymbol: true,
      showDecimals: true,
    }),
    discountPrice: useFormatPrice({
      amount: priceInformation?.discount?.product / 100 || 0,
      noCurrencySymbol: true,
      showDecimals: true,
    }),
    registrationFeePrice: useFormatPrice({
      amount: priceInformation.registration_fee / 100 || 0,
      noCurrencySymbol: true,
      showDecimals: true,
    }),
    nextPeriodPrice: useFormatPrice({
      amount: priceInformation.next_period?.price / 100 || 0,
      noCurrencySymbol: true,
      showDecimals: true,
    }),
    minAmount: useFormatPrice({
      amount: priceInformation.min_amount / 100 || 0,
      noCurrencySymbol: true,
      showDecimals: true,
    }),
  };

  const isNormalTermination =
    terminationRule === TerminationRule.NORMAL_TERMINATION;

  const isLastDayOfNextMonthTermination =
    terminationRule === TerminationRule.TERMINATION_LAST_DAY_OF_NEXT_MONTH;

  const isHalfPriceTwoMonthsDiscount =
    priceInformation.discount_type === DiscountType.HALV_PRICE_2_MONTHS;

  const hasFreeRegistration = Boolean(
    priceInformation.discount?.free_registration
  );

  const currentDate = new Date();

  const endOfCurrentMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  );

  return (
    <Fragment>
      {showFirstHalf && (
        <Fragment>
          <div className={PaymentToday}>
            <Typography variant="body1" fontWeight={900}>
              {isNormalTermination || forSecondStep
                ? t('toPayToday', { ns: 'campaign' })
                : t('becomeMember', { ns: 'campaign' })}
            </Typography>

            {!isHalfPriceTwoMonthsDiscount && (
              <Typography variant="body1" fontWeight={900}>
                0 {currencySymbol}
              </Typography>
            )}
          </div>

          {isNormalTermination && (
            <Typography variant="body1" mb={2}>
              {t('freePlanUntil', {
                planName: priceInformation.plan_name,
                ns: 'campaign',
              })}{' '}
              {format(priceInformation.discount_period.end, 'dd.MM.yyyy')}
            </Typography>
          )}

          {isLastDayOfNextMonthTermination && !isHalfPriceTwoMonthsDiscount && (
            <Fragment>
              <Typography variant="body1">
                {t('forAWholeMonth', {
                  planName: priceInformation.plan_name,
                  planPrice: formattedPrices.planPrice,
                  ns: 'campaign',
                })}{' '}
              </Typography>

              <Typography variant="body1" mb={2}>
                {t('freeRegistration', {
                  registration_fee: formattedPrices.registrationFeePrice,
                  ns: 'campaign',
                })}{' '}
              </Typography>
            </Fragment>
          )}

          {isHalfPriceTwoMonthsDiscount && (
            <Box mb={2}>
              <Box display="flex" gap={0.75} mt={1}>
                <Typography variant="body1">
                  {`
                  ${priceInformation.plan_name}
                  (${format(
                    priceInformation?.discount_period?.start,
                    'dd.MM.yyyy'
                  )} - ${format(endOfCurrentMonth, 'dd.MM.yyyy')})
                `}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{ textDecoration: 'line-through' }}
                >
                  {`${formattedPrices.planPriceRestMonth} ${currencySymbol}`}
                </Typography>

                <Typography variant="body1">
                  {`${formattedPrices.discountPrice} ${currencySymbol}`}
                </Typography>
              </Box>

              <Box display="flex" gap={0.5}>
                <Typography variant="body1">
                  {t('signUpFee', { ns: 'checkout' })}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    ...(hasFreeRegistration && {
                      textDecoration: 'line-through',
                    }),
                  }}
                >
                  {`${formattedPrices.registrationFeePrice} ${currencySymbol}`}
                </Typography>

                {hasFreeRegistration && (
                  <Typography variant="body1">
                    {`0 ${currencySymbol}`}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Fragment>
      )}

      {showSecondHalf && !isHalfPriceTwoMonthsDiscount && (
        <Fragment>
          {isNormalTermination && (
            <Typography variant="body1" marginTop={1}>
              {t('summary', {
                firstPayment: format(
                  priceInformation.first_payment,
                  'dd.MM.yyyy'
                ),
                nextPeriodPrice: formattedPrices.nextPeriodPrice,
                currencySymbol: currencySymbol,
                nextPeriodStart: format(
                  priceInformation.next_period.start,
                  'dd.MM.yyyy'
                ),
                nextPeriodEnd: format(
                  priceInformation.next_period.end,
                  'dd.MM.yyyy'
                ),
                fullPlanPrice: formattedPrices.planPrice,
                firstFullPaymentDate: format(
                  priceInformation.first_full_payment,
                  'dd.MM.yyyy'
                ),
                firstCancelDate: format(
                  priceInformation.first_cancel_date,
                  'dd.MM.yyyy'
                ),
                ns: 'campaign',
              })}
            </Typography>
          )}

          {isLastDayOfNextMonthTermination && (
            <Typography variant="body1" marginTop={1}>
              {t('minimumPrice', {
                first_termination: format(
                  priceInformation.first_termination,
                  'dd.MM.yyyy'
                ),
                min_amount: formattedPrices.minAmount,
                plan_price: formattedPrices.planPrice,
              })}
            </Typography>
          )}
        </Fragment>
      )}

      {showSecondHalf && isHalfPriceTwoMonthsDiscount && (
        <Fragment>
          <Typography variant="body2" marginTop={1}>
            {t('50%&FreeRegistration', {
              first_full_payment: format(
                priceInformation.first_full_payment,
                'dd.MM.yyyy'
              ),
            })}
          </Typography>

          <Typography variant="body2">
            {t('minimumPriceWithDiscount', {
              min_amount: formattedPrices.minAmount,
              discount_period: format(
                priceInformation.discount_period.end,
                'dd.MM.yyyy'
              ),
              plan_price: formattedPrices.planPrice,
            })}
          </Typography>

          {isLastDayOfNextMonthTermination && (
            <Typography variant="body2">
              {t('noticePeriodePlus1Month')}
            </Typography>
          )}
        </Fragment>
      )}
    </Fragment>
  );
};

export default CampaignSummary;
